"use client";

import { useEffect } from 'react';
import { useStore } from '@/store';
import { fetchAccount, readToken, toStoreUser } from '@/api/auth';

/**
 * Restores a session on page load. It asks /auth/me, which is the only endpoint
 * that can confirm a token is still valid, and dispatches LOGIN_SUCCESS rather
 * than SET_PROFILE because only the former restores `isAuthenticated`.
 */
export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { state, dispatch } = useStore();

  useEffect(() => {
    const token = readToken();
    if (!token) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    let cancelled = false;
    dispatch({ type: 'SET_LOADING', payload: true });

    fetchAccount()
      .then(({ account }) => {
        if (cancelled) {
          return;
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: toStoreUser(account), token },
        });
      })
      .catch(() => {
        if (!cancelled) {
          dispatch({ type: 'LOGOUT' });
        }
      })
      .finally(() => {
        if (!cancelled) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (state.loading && !state.auth.user && readToken()) {
    return <div className="h-screen flex items-center justify-center">Loading...</div>;
  }

  return <>{children}</>;
}
